import { useEffect, useRef, useState } from 'react';
import type { ClipboardEvent, FormEvent, KeyboardEvent, ReactNode } from 'react';
import { ApiError } from '../../lib/api';
import {
  OTP_DEMO_CODE,
  OTP_LENGTH,
  OTP_MAX_ATTEMPTS,
  OTP_RESEND_SECONDS,
  resendOtp,
} from '../../lib/auth';

interface Props {
  /** Who is signing in; shown so a shared computer makes the account obvious. */
  email: string;
  /** Defaults describe the sign-in step; Forgot password words its own. */
  title?: string;
  description?: ReactNode;
  simulatedNote?: ReactNode;
  /** Rejects when the code is wrong. */
  onVerify: (code: string) => Promise<void>;
  onCancel: () => void;
  /** Too many wrong codes: the sign-in starts over. */
  onLocked: () => void;
}

const BLANK = () => Array<string>(OTP_LENGTH).fill('');

/**
 * The verification-code step shown after a correct password.
 *
 * One box per digit: typing moves on, Backspace moves back, and a pasted or
 * auto-filled code lands across all the boxes at once.
 */
export function OtpDialog({
  email,
  title = 'Verify it’s you',
  description,
  simulatedNote,
  onVerify,
  onCancel,
  onLocked,
}: Props) {
  const [digits, setDigits] = useState<string[]>(BLANK);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [wait, setWait] = useState(OTP_RESEND_SECONDS);

  const boxes = useRef<(HTMLInputElement | null)[]>([]);
  const panel = useRef<HTMLFormElement>(null);

  useEffect(() => {
    boxes.current[0]?.focus();
  }, []);

  // After a wrong code the boxes come back enabled and empty; put the caret in
  // the first one. An effect, because the boxes are still disabled at the
  // moment the failure is handled.
  useEffect(() => {
    if (!verifying && attempts > 0) boxes.current[0]?.focus();
  }, [verifying, attempts]);

  // Count down to "Send again".
  useEffect(() => {
    if (wait <= 0) return;
    const id = window.setTimeout(() => setWait((s) => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [wait]);

  const code = digits.join('');
  const complete = code.length === OTP_LENGTH;

  /** Writes digits starting at `from`; anything that is not a digit is dropped. */
  function fill(from: number, text: string) {
    const clean = text.replace(/\D/g, '');
    if (!clean) return;

    setDigits((prev) => {
      const next = [...prev];
      for (let i = 0; i < clean.length && from + i < OTP_LENGTH; i++) next[from + i] = clean[i];
      return next;
    });
    setError(null);
    boxes.current[Math.min(from + clean.length, OTP_LENGTH - 1)]?.focus();
  }

  function onChange(index: number, value: string) {
    if (value.replace(/\D/g, '') === '') {
      setDigits((prev) => prev.map((d, i) => (i === index ? '' : d)));
      return;
    }
    fill(index, value);
  }

  function onBoxKeyDown(index: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && digits[index] === '' && index > 0) {
      e.preventDefault();
      setDigits((prev) => prev.map((d, i) => (i === index - 1 ? '' : d)));
      boxes.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      boxes.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      e.preventDefault();
      boxes.current[index + 1]?.focus();
    }
  }

  function onPaste(e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    fill(0, e.clipboardData.getData('text'));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!complete || verifying) return;

    setError(null);
    setNotice(null);
    setVerifying(true);
    try {
      await onVerify(code);
    } catch (err) {
      const used = attempts + 1;
      setAttempts(used);

      if (used >= OTP_MAX_ATTEMPTS) {
        onLocked();
        return;
      }

      const left = OTP_MAX_ATTEMPTS - used;
      const reason = err instanceof ApiError ? err.message : 'The code could not be verified.';
      setError(`${reason} ${left} ${left === 1 ? 'attempt' : 'attempts'} left.`);
      setDigits(BLANK());
      setVerifying(false);
    }
  }

  async function onResend() {
    if (wait > 0) return;
    await resendOtp();
    setDigits(BLANK());
    setError(null);
    setNotice('A new code was sent.');
    setWait(OTP_RESEND_SECONDS);
    boxes.current[0]?.focus();
  }

  /** Esc backs out; Tab stays inside the dialog. */
  function onDialogKeyDown(e: KeyboardEvent<HTMLFormElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
      return;
    }
    if (e.key !== 'Tab') return;

    const items = panel.current?.querySelectorAll<HTMLElement>(
      'input:not(:disabled), button:not(:disabled)',
    );
    if (!items || items.length === 0) return;

    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return (
    <div className="otp-overlay">
      <form
        ref={panel}
        className="otp-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="otp-title"
        aria-describedby="otp-desc"
        onSubmit={onSubmit}
        onKeyDown={onDialogKeyDown}
        noValidate
      >
        <h2 id="otp-title" className="otp-title">
          {title}
        </h2>
        <p id="otp-desc" className="otp-desc">
          {description ?? (
            <>
              Enter the {OTP_LENGTH}-digit code from your Microsoft Authenticator app for{' '}
              <b>{email}</b>.
            </>
          )}
        </p>

        {error && (
          <div className="alert" role="alert">
            {error}
          </div>
        )}
        {notice && !error && (
          <p className="otp-notice" role="status">
            {notice}
          </p>
        )}

        <div className={error ? 'otp-boxes otp-boxes--error' : 'otp-boxes'}>
          {digits.map((digit, i) => (
            <input
              key={i}
              ref={(el) => {
                boxes.current[i] = el;
              }}
              className="otp-box"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              maxLength={OTP_LENGTH}
              aria-label={`Digit ${i + 1} of ${OTP_LENGTH}`}
              value={digit}
              disabled={verifying}
              onChange={(e) => onChange(i, e.target.value)}
              onKeyDown={(e) => onBoxKeyDown(i, e)}
              onPaste={onPaste}
              onFocus={(e) => e.target.select()}
            />
          ))}
        </div>

        <div className="otp-actions">
          <button type="submit" className="btn-signin" disabled={!complete || verifying}>
            {verifying ? 'Verifying…' : 'Verify'}
          </button>
          <button type="button" className="btn-forgot" onClick={onCancel}>
            Cancel
          </button>
        </div>

        <p className="otp-resend">
          Didn&apos;t get a code?{' '}
          <button type="button" className="otp-link" onClick={onResend} disabled={wait > 0}>
            {wait > 0 ? `Send again in ${wait}s` : 'Send again'}
          </button>
        </p>

        <p className="otp-sim">
          {simulatedNote ?? (
            <>
              Microsoft 365 verification is simulated for now — use <code>{OTP_DEMO_CODE}</code>.
            </>
          )}
        </p>
      </form>
    </div>
  );
}
